import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MapPin, TrendingUp, Target, Star } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { JsonLd } from "@/components/JsonLd";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const SEOMagnoliaPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  const canonicalUrl = generateCanonicalUrl('/seo-magnolia');

  const content = {
    es: {
      title: "SEO Magnolia TX | Posicionamiento Local en Google Maps – Luis ROC",
      description: "Servicios de SEO local en Magnolia, Texas. Aumenta tu visibilidad en Google Maps y búsquedas locales. Reunión inicial gratuita. Llama (832) 521-5050.",
      h1: "SEO en Magnolia TX — Posiciona tu Negocio Local",
      intro: "Magnolia es una comunidad en crecimiento cerca de The Woodlands. Optimiza tu presencia en Google Maps y búsquedas locales para capturar clientes en los ZIP 77354, 77355 y áreas cercanas.",
      ctaButton: "Reunión inicial gratuita de 30 minutos",
      contactButton: "Solicitar Consulta",
      ctaTitle: "Lidera Google en Magnolia",
      breadcrumbs: [
        { name: "Servicios", url: "/servicios" },
        { name: "Localidades", url: "/servicios-locales-houston" },
        { name: "SEO Magnolia", url: "/seo-magnolia" }
      ]
    },
    en: {
      title: "SEO Magnolia TX | Local Google Maps Ranking – Luis ROC",
      description: "Local SEO services in Magnolia, Texas. Increase your visibility on Google Maps and local searches. Free 30-minute initial consultation. Call (832) 521-5050.",
      h1: "SEO in Magnolia TX — Rank Your Local Business",
      intro: "Magnolia is a growing community near The Woodlands. Optimize your presence on Google Maps and local searches to capture customers in ZIP codes 77354, 77355, and nearby areas.",
      ctaButton: "Free 30-Minute Initial Consultation",
      contactButton: "Request Consultation",
      ctaTitle: "Lead Google in Magnolia",
      breadcrumbs: [
        { name: "Services", url: "/en/services" },
        { name: "Locations", url: "/servicios-locales-houston" },
        { name: "SEO Magnolia", url: "/seo-magnolia" }
      ]
    }
  };

  const t = content[language];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "SEO en Magnolia TX",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": {
      "@type": "City",
      "name": "Magnolia",
      "containedIn": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "description": t.description
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        <meta name="keywords" content="seo magnolia tx, seo magnolia texas, agencia seo magnolia, posicionamiento google magnolia, seo local magnolia" />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={t.title} />
        <meta property="og:description" content={t.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Magnolia" />
      </Helmet>

      <JsonLd data={serviceSchema} />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main className="container mx-auto px-4 py-8">
          <BreadcrumbNav items={t.breadcrumbs} language={language} />

          {/* Hero Section */}
          <section className="py-16 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              {t.h1}
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              {t.intro}
            </p>

            <Button 
              size="lg" 
              onClick={() => window.location.href = language === 'es' ? '/diagnostico-seo-gratis' : '/en/free-seo-diagnosis'}
            >
              {t.ctaButton}
            </Button>
          </section>

          {/* Services Grid */}
          <section className="py-16">
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <Card className="p-6 text-center">
                <MapPin className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">SEO Local</h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'es' ? 'Optimización Google Maps' : 'Google Maps Optimization'}
                </p>
              </Card>
              
              <Card className="p-6 text-center">
                <Target className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">{language === 'es' ? 'Auditoría Técnica' : 'Technical Audit'}</h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'es' ? 'Análisis completo del sitio' : 'Complete site analysis'}
                </p>
              </Card>
              
              <Card className="p-6 text-center">
                <TrendingUp className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">{language === 'es' ? 'Resultados Medibles' : 'Measurable Results'}</h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'es' ? 'Medición orientada a negocio' : 'Business-focused measurement'}
                </p>
              </Card>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star className="h-6 w-6" fill="currentColor" />
              <Star className="h-6 w-6" fill="currentColor" />
              <Star className="h-6 w-6" fill="currentColor" />
              <Star className="h-6 w-6" fill="currentColor" />
              <Star className="h-6 w-6" fill="currentColor" />
            </div>
            <h2 className="text-3xl font-bold mb-4">{t.ctaTitle}</h2>
            <Button 
              size="lg" 
              variant="secondary" 
              onClick={() => window.location.href = language === 'es' ? '/contacto' : '/en/contact'}
            >
              {t.contactButton}
            </Button>
          </section>
        </main>

        <KnowledgeConnections path="/seo-magnolia" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default SEOMagnoliaPage;
